/**
 * Billing > Settings (Phase 21 / Task 21.7a): Paelode's invoice letterhead, terms, numbering,
 * tax and sender. GET / PUT /v1/platform/billing/settings.
 *
 * The sender block shows the send-time check (Task 21.5, DEC-327 D8): when SES would refuse
 * the from-address, a red warning says so BEFORE anyone presses Send on an invoice.
 *
 * OPEN-212 PR A (DEC-387 D7): Paelode's bank details for customers who pay by hand. They print
 * only on the invoices of a customer whose account has "Show bank details on invoices" on. The
 * account number is write-only: the page shows the last four on file, a blank field keeps it.
 */
import * as React from 'react';
import { toast } from 'sonner';
import { Button } from 'src/components/ui/button';
import {
  PLATFORM_BILLING_LOGO_MAX_CHARS,
  PLATFORM_BILLING_LOGO_MIME_TYPES,
  isPrintableLogoDataUri,
} from '@lodex-pro/shared/platform-billing';
import { isAbaRoutingNumber, isBankAccountNumber } from '@lodex-pro/shared/bank-numbers';
import { FormRow, Pill, fieldStyle, subText } from '../../ui/bits';
import {
  useBillingSettings,
  useSaveBillingSettings,
  type BillingSettings,
  type SenderView,
  type SettingsUpdate,
} from './billing-hooks';
import { billingErrorText, bpsToPercent, percentToBps } from './billing-format';

const PREFIX_RE = /^[A-Z]{1,6}$/;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

interface Draft {
  legalName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  logoDataUri: string | null;
  remitInstructions: string;
  remitBankName: string;
  remitRoutingNumber: string;
  /** A NEW account number; blank keeps the stored one. */
  remitAccountNumber: string;
  clearRemitAccount: boolean;
  remitEmail: string;
  footerText: string;
  defaultTermsDays: string;
  invoicePrefix: string;
  creditNotePrefix: string;
  taxRate: string;
  fromAddress: string;
  fromDisplayName: string;
  replyTo: string;
  billingTimezone: string;
}

function toDraft(s: BillingSettings): Draft {
  return {
    legalName: s.legalName,
    addressLine1: s.addressLine1 ?? '',
    addressLine2: s.addressLine2 ?? '',
    city: s.city ?? '',
    state: s.state ?? '',
    postalCode: s.postalCode ?? '',
    logoDataUri: s.logoDataUri,
    remitInstructions: s.remitInstructions ?? '',
    remitBankName: s.remitBankName ?? '',
    remitRoutingNumber: s.remitRoutingNumber ?? '',
    remitAccountNumber: '',
    clearRemitAccount: false,
    remitEmail: s.remitEmail ?? '',
    footerText: s.footerText ?? '',
    defaultTermsDays: String(s.defaultTermsDays),
    invoicePrefix: s.invoicePrefix,
    creditNotePrefix: s.creditNotePrefix,
    taxRate: bpsToPercent(s.taxRateBps).replace(/%$/, ''),
    fromAddress: s.fromAddress,
    fromDisplayName: s.fromDisplayName,
    replyTo: s.replyTo ?? '',
    billingTimezone: s.billingTimezone,
  };
}

const orNull = (v: string): string | null => (v.trim() === '' ? null : v.trim());

/** The draft's problems, keyed by field; empty when it can be saved. */
export function settingsErrors(d: Draft): Partial<Record<keyof Draft, string>> {
  const e: Partial<Record<keyof Draft, string>> = {};
  if (d.legalName.trim() === '') e.legalName = 'Required';
  if (!/^\d{1,3}$/.test(d.defaultTermsDays.trim()) || Number(d.defaultTermsDays) > 365) {
    e.defaultTermsDays = 'A whole number of days, 0 to 365';
  }
  if (!PREFIX_RE.test(d.invoicePrefix.trim().toUpperCase())) e.invoicePrefix = '1 to 6 letters';
  if (!PREFIX_RE.test(d.creditNotePrefix.trim().toUpperCase())) {
    e.creditNotePrefix = '1 to 6 letters';
  }
  if (percentToBps(d.taxRate) === null) e.taxRate = 'A percentage from 0 to 100, e.g. 8.25';
  if (!EMAIL_RE.test(d.fromAddress.trim())) e.fromAddress = 'Enter a valid email address';
  if (d.fromDisplayName.trim() === '') e.fromDisplayName = 'Required';
  if (d.replyTo.trim() !== '' && !EMAIL_RE.test(d.replyTo.trim())) {
    e.replyTo = 'Enter a valid email address';
  }
  if (d.billingTimezone.trim() === '') e.billingTimezone = 'Required';
  const routing = d.remitRoutingNumber.trim();
  if (routing !== '' && !isAbaRoutingNumber(routing)) {
    e.remitRoutingNumber = 'A 9-digit ABA routing number (check the digits)';
  }
  const account = d.remitAccountNumber.trim();
  if (account !== '' && !isBankAccountNumber(account)) {
    e.remitAccountNumber = '4 to 17 digits, no spaces or dashes';
  }
  if (d.remitEmail.trim() !== '' && !EMAIL_RE.test(d.remitEmail.trim())) {
    e.remitEmail = 'Enter a valid email address';
  }
  return e;
}

/** The PUT body for a valid draft (the API uppercases the prefixes too). */
/**
 * The PUT body. `loadedLogo` is the logo the page loaded: an unchanged logo is left out (the
 * PUT keeps the stored one), so an old row the current rule refuses, such as an SVG saved
 * before #940, cannot block saving unrelated fields (#945 review LOW-2).
 */
export function settingsBody(d: Draft, loadedLogo?: string | null): SettingsUpdate {
  return {
    legalName: d.legalName.trim(),
    addressLine1: orNull(d.addressLine1),
    addressLine2: orNull(d.addressLine2),
    city: orNull(d.city),
    state: orNull(d.state),
    postalCode: orNull(d.postalCode),
    ...(loadedLogo !== undefined && d.logoDataUri === loadedLogo
      ? {}
      : { logoDataUri: d.logoDataUri }),
    remitInstructions: orNull(d.remitInstructions),
    remitBankName: orNull(d.remitBankName)?.toUpperCase() ?? null,
    remitRoutingNumber: orNull(d.remitRoutingNumber),
    // Write-only: a typed number replaces the stored one, Clear removes it, blank keeps it.
    ...(d.clearRemitAccount
      ? { remitAccountNumber: null }
      : d.remitAccountNumber.trim() === ''
        ? {}
        : { remitAccountNumber: d.remitAccountNumber.trim() }),
    remitEmail: orNull(d.remitEmail)?.toLowerCase() ?? null,
    footerText: orNull(d.footerText),
    defaultTermsDays: Number(d.defaultTermsDays.trim()),
    invoicePrefix: d.invoicePrefix.trim().toUpperCase(),
    creditNotePrefix: d.creditNotePrefix.trim().toUpperCase(),
    taxRateBps: percentToBps(d.taxRate) ?? 0,
    fromAddress: d.fromAddress.trim().toLowerCase(),
    fromDisplayName: d.fromDisplayName.trim(),
    replyTo: orNull(d.replyTo)?.toLowerCase() ?? null,
    billingTimezone: d.billingTimezone.trim(),
  };
}

const SENDER_REASONS: Readonly<Record<string, string>> = {
  NOT_VERIFIED: 'is not verified in SES',
  NOT_ALLOWED_DOMAIN: 'is not on a domain this service may send from',
  LOOKUP_FAILED: 'could not be checked with SES just now',
};

function SenderStatus({ sender }: { sender: SenderView }): React.JSX.Element {
  if (sender.status === null) {
    return (
      <p style={subText} data-testid="sender-unchecked">
        The sender is not checked on this environment.
      </p>
    );
  }
  if (sender.status.ok) {
    return (
      <p data-testid="sender-ok">
        <Pill tone="positive" label="Sender verified" />{' '}
        <span style={subText}>
          Invoices go from {sender.displayName} &lt;{sender.address}&gt;.
        </span>
      </p>
    );
  }
  const why = SENDER_REASONS[sender.status.reason ?? ''] ?? 'cannot be used';
  return (
    <div
      role="alert"
      data-testid="sender-not-verified"
      style={{
        border: '1px solid var(--color-danger)',
        borderRadius: '12px',
        padding: '10px 12px',
        color: 'var(--color-danger)',
      }}
    >
      Invoices will not send: {sender.address} {why} (domain {sender.status.domain}). Fix the
      from-address below or verify it in SES before sending.
    </div>
  );
}

function TextField({
  id,
  label,
  value,
  onChange,
  error,
  hint,
  required,
  multiline,
  className,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string | undefined;
  hint?: string | undefined;
  required?: boolean | undefined;
  multiline?: boolean | undefined;
  className?: string | undefined;
}): React.JSX.Element {
  const common = {
    id,
    value,
    style: fieldStyle,
    'aria-invalid': error !== undefined,
  };
  return (
    <FormRow
      label={label}
      htmlFor={id}
      {...(hint !== undefined ? { hint } : {})}
      {...(required !== undefined ? { required } : {})}
    >
      {multiline === true ? (
        <textarea
          {...common}
          rows={3}
          onChange={(e): void => onChange(e.target.value)}
          style={{ ...fieldStyle, resize: 'vertical' }}
        />
      ) : (
        <input {...common} className={className} onChange={(e): void => onChange(e.target.value)} />
      )}
      {error !== undefined && (
        <p role="alert" style={{ color: 'var(--color-danger)', fontSize: '12px' }}>
          {error}
        </p>
      )}
    </FormRow>
  );
}

export function BillingSettingsPage(): React.JSX.Element {
  const { data, isLoading, isError } = useBillingSettings();
  const [draft, setDraft] = React.useState<Draft | null>(null);

  React.useEffect(() => {
    if (data !== undefined) setDraft(toDraft(data.settings));
  }, [data]);

  const save = useSaveBillingSettings({
    onSuccess: () => toast.success('Billing settings saved.'),
    onError: (err) => toast.error(billingErrorText(err, 'Could not save. Nothing was changed.')),
  });

  if (isLoading) return <p style={{ padding: '24px', ...subText }}>Loading…</p>;
  if (isError || data === undefined || draft === null) {
    return (
      <p role="alert" style={{ padding: '24px', color: 'var(--color-danger)' }}>
        Could not load the billing settings.
      </p>
    );
  }

  const set =
    (k: keyof Draft) =>
    (v: string): void =>
      setDraft({ ...draft, [k]: v });
  const errors = settingsErrors(draft);
  const invalid = Object.keys(errors).length > 0;

  const onLogo = (file: File | undefined): void => {
    if (file === undefined) return;
    const reader = new FileReader();
    reader.onload = (): void => {
      const uri = typeof reader.result === 'string' ? reader.result : '';
      // #940 review: the same rule the API and the invoice renderer use, so a logo accepted
      // here always prints (an SVG used to preview here and then print as nothing).
      if (uri.length > PLATFORM_BILLING_LOGO_MAX_CHARS) {
        toast.error('That image is too large for an invoice logo. Use a smaller file.');
      } else if (!isPrintableLogoDataUri(uri)) {
        toast.error('Use a PNG, JPEG, GIF or WebP image. Invoices cannot print an SVG.');
      } else {
        // Functional: a field edited while a large file was being read is kept.
        setDraft((d) => (d === null ? d : { ...d, logoDataUri: uri }));
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div style={{ maxWidth: '720px' }}>
      <section style={{ marginBottom: '20px' }}>
        <h3 style={{ fontSize: '15px', fontWeight: 600, margin: '0 0 8px' }}>Sender</h3>
        <SenderStatus sender={data.sender} />
        {!data.settings.persisted && (
          <p style={{ ...subText, marginTop: '8px' }} data-testid="settings-defaults">
            These are the defaults. Nothing has been saved yet.
          </p>
        )}
      </section>

      <form
        onSubmit={(e): void => {
          e.preventDefault();
          if (invalid) return;
          save.mutate(settingsBody(draft, data.settings.logoDataUri));
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}
      >
        <h3 style={{ fontSize: '15px', fontWeight: 600, margin: '8px 0 0' }}>Letterhead</h3>
        <TextField
          id="bs-legal-name"
          label="Legal name"
          required
          value={draft.legalName}
          onChange={set('legalName')}
          error={errors.legalName}
        />
        <TextField
          id="bs-address1"
          label="Address line 1"
          value={draft.addressLine1}
          onChange={set('addressLine1')}
        />
        <TextField
          id="bs-address2"
          label="Address line 2"
          value={draft.addressLine2}
          onChange={set('addressLine2')}
        />
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '10px' }}>
          <TextField id="bs-city" label="City" value={draft.city} onChange={set('city')} />
          <TextField id="bs-state" label="State" value={draft.state} onChange={set('state')} />
          <TextField
            id="bs-postal"
            label="ZIP"
            value={draft.postalCode}
            onChange={set('postalCode')}
          />
        </div>
        <FormRow label="Logo" htmlFor="bs-logo" hint="Printed at the top of every invoice.">
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {draft.logoDataUri !== null && (
              <img
                src={draft.logoDataUri}
                alt="Current invoice logo"
                style={{ maxHeight: '48px', maxWidth: '160px' }}
              />
            )}
            <input
              id="bs-logo"
              type="file"
              accept={PLATFORM_BILLING_LOGO_MIME_TYPES.join(',')}
              onChange={(e): void => onLogo(e.target.files?.[0])}
            />
            {draft.logoDataUri !== null && (
              <Button
                type="button"
                variant="outline"
                onClick={(): void => setDraft({ ...draft, logoDataUri: null })}
              >
                Remove logo
              </Button>
            )}
          </div>
        </FormRow>
        <TextField
          id="bs-remit"
          label="How to pay (remit instructions)"
          multiline
          value={draft.remitInstructions}
          onChange={set('remitInstructions')}
        />
        <h3 style={{ fontSize: '15px', fontWeight: 600, margin: '8px 0 0' }}>
          Bank details for paying by hand
        </h3>
        <p style={{ ...subText, margin: 0 }}>
          For customers who pay by wire or by their own ACH transfer. These print only on the
          invoices of a customer whose account has &quot;Show bank details on invoices&quot; on.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <TextField
            id="bs-remit-bank"
            label="Bank name"
            value={draft.remitBankName}
            onChange={set('remitBankName')}
          />
          <TextField
            id="bs-remit-routing"
            label="Routing number (ABA)"
            value={draft.remitRoutingNumber}
            onChange={set('remitRoutingNumber')}
            error={errors.remitRoutingNumber}
          />
          <TextField
            id="bs-remit-account"
            label="Account number"
            value={draft.remitAccountNumber}
            onChange={set('remitAccountNumber')}
            error={errors.remitAccountNumber}
            hint={
              data.settings.remitAccountLast4 === null
                ? 'None on file.'
                : `•••• ${data.settings.remitAccountLast4} on file. Leave blank to keep it.`
            }
          />
          <TextField
            id="bs-remit-email"
            label="Remittance advice email"
            value={draft.remitEmail}
            onChange={set('remitEmail')}
            error={errors.remitEmail}
          />
        </div>
        {data.settings.remitAccountLast4 !== null && (
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
            <input
              type="checkbox"
              checked={draft.clearRemitAccount}
              onChange={(e): void => setDraft({ ...draft, clearRemitAccount: e.target.checked })}
            />
            Remove the account number on file
          </label>
        )}
        <TextField
          id="bs-footer"
          label="Footer text"
          multiline
          value={draft.footerText}
          onChange={set('footerText')}
        />

        <h3 style={{ fontSize: '15px', fontWeight: 600, margin: '8px 0 0' }}>
          Terms and numbering
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '10px' }}>
          <TextField
            id="bs-terms"
            label="Payment terms (days)"
            value={draft.defaultTermsDays}
            onChange={set('defaultTermsDays')}
            error={errors.defaultTermsDays}
          />
          <TextField
            id="bs-invoice-prefix"
            label="Invoice prefix"
            value={draft.invoicePrefix}
            onChange={set('invoicePrefix')}
            error={errors.invoicePrefix}
          />
          <TextField
            id="bs-credit-prefix"
            label="Credit note prefix"
            value={draft.creditNotePrefix}
            onChange={set('creditNotePrefix')}
            error={errors.creditNotePrefix}
          />
          <TextField
            id="bs-tax"
            label="Tax rate (%)"
            value={draft.taxRate}
            onChange={set('taxRate')}
            error={errors.taxRate}
          />
        </div>
        <TextField
          id="bs-timezone"
          className="keep-case"
          label="Billing time zone"
          hint="Decides which day an invoice is issued, due and paid on (e.g. America/Chicago)."
          value={draft.billingTimezone}
          onChange={set('billingTimezone')}
          error={errors.billingTimezone}
        />

        <h3 style={{ fontSize: '15px', fontWeight: 600, margin: '8px 0 0' }}>Email</h3>
        <TextField
          id="bs-from"
          className="keep-case"
          label="Send invoices from"
          required
          value={draft.fromAddress}
          onChange={set('fromAddress')}
          error={errors.fromAddress}
        />
        <TextField
          id="bs-from-name"
          className="keep-case"
          label="Sender name"
          required
          value={draft.fromDisplayName}
          onChange={set('fromDisplayName')}
          error={errors.fromDisplayName}
        />
        <TextField
          id="bs-reply-to"
          className="keep-case"
          label="Replies go to"
          hint="Customer replies and payment alerts go here. Empty: replies go to the sender address and alerts to each platform admin."
          value={draft.replyTo}
          onChange={set('replyTo')}
          error={errors.replyTo}
        />

        <div>
          <Button type="submit" disabled={invalid || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save settings'}
          </Button>
        </div>
      </form>
    </div>
  );
}
